import { ApiResponse, ApiResponseArray } from '@/models/ApiResponse';
import {
  ClassificationTag,
  FundCompany,
  LegalEntity,
  UserInitiator,
} from '@/models/FundLevel';
import { ApprovalUser } from '@/models/FundLevel/ApprovalUser';
import {
  TradeRequestApproval,
  TradeRequestExecuted,
  TradeRequestExecution,
  TradeRequestMismatch,
  TradeRequestPostExecution,
} from '@/models/FundLevel/TradeNotifications';
import { ReportFile } from '@/models/ReportFile';
import {
  ApprovedTradeRequestGroupingPayload,
  BlotterUploadResult,
  BulkTradeNotificationsPayload,
  DownloadDocumentForFundPayload,
  DownloadExecutedTradesReportRequestPayload,
  DownloadExecutionReportRequestPayload,
  FieldExclusionData,
  FixReferenceData,
  HideSelectedTradesPayload,
  InsertClientApprovedStatusPayload,
  InsertPMCRMApprovedStatusPayload,
  InsertSentForExecutionStatusPayload,
  NewTradeRequestStatusPayload,
  RemoveTradeRequestGroupingPayload,
  TransactionFilesPostRequestPayload,
  TransactionFilesRequestPayload,
  UpdateTradeRequestPayload,
  UpdateTradeRequestResponse,
  UpdateTradeRequestStatusPayload,
  UploadTradeRequestPayload,
  WorkflowStats,
} from '@pages/TradeNotification/store/types';
import { AxiosInstance } from 'axios';

export default class TradeNotificationAPI {
  client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  async fetchApprovalTradeRequests(): Promise<
    ApiResponseArray<TradeRequestApproval>
  > {
    const res = await this.client.get<ApiResponseArray<TradeRequestApproval>>(
      '/v2/fund-level-hedging/get-all-approval-trade-requests/',
    );
    return res.data;
  }

  async fetchExecutableTradeRequests(): Promise<
    ApiResponseArray<TradeRequestExecution>
  > {
    const res = await this.client.get<ApiResponseArray<TradeRequestExecution>>(
      '/v2/fund-level-hedging/get-all-executable-trade-requests/',
    );
    return res.data;
  }

  async checkFixConnection(): Promise<string> {
    const res = await this.client.post<ApiResponse<string>>(
      '/v2/fund-level-hedging/check-fix-connection/',
      {},
    );
    return res.data.data;
  }

  async fetchPostExecutionTradeRequests(): Promise<
    ApiResponseArray<TradeRequestPostExecution>
  > {
    const res = await this.client.get<
      ApiResponseArray<TradeRequestPostExecution>
    >('/v2/fund-level-hedging/get-all-post-execution-trade-requests/');
    return res.data;
  }

  async fetchCancelledTradeRequests(): Promise<
    ApiResponseArray<TradeRequestPostExecution>
  > {
    const res = await this.client.get<
      ApiResponseArray<TradeRequestPostExecution>
    >('/v2/fund-level-hedging/get-all-cancelled-trade-requests/');
    return res.data;
  }

  async fetchExecutedDataMismatchTradeRequests(): Promise<
    ApiResponseArray<TradeRequestMismatch>
  > {
    const res = await this.client.get<ApiResponseArray<TradeRequestMismatch>>(
      '/v2/fund-level-hedging/get-all-executed-data-mismatch-trade-requests/',
    );
    return res.data;
  }

  async fetchExecutedTradeRequests(): Promise<
    ApiResponseArray<TradeRequestExecuted>
  > {
    const res = await this.client.get<ApiResponseArray<TradeRequestExecuted>>(
      '/v2/fund-level-hedging/get-all-executed-trade-requests/',
    );
    return res.data;
  }

  async fetchFixReferenceData(): Promise<ApiResponseArray<FixReferenceData>> {
    const res = await this.client.get<ApiResponseArray<FixReferenceData>>(
      '/v2/fund-level-hedging/get-fix-reference-data/',
    );
    return res.data;
  }

  async fetchFundCompanies(): Promise<ApiResponseArray<FundCompany>> {
    const res = await this.client.get<ApiResponseArray<FundCompany>>(
      '/v2/fund-level-hedging/fund-companies-all/',
    );
    return res.data;
  }

  async fetchClassificationTags(): Promise<
    ApiResponseArray<ClassificationTag>
  > {
    const res = await this.client.get<ApiResponseArray<ClassificationTag>>(
      '/v2/fund-level-hedging/classification-tags/',
    );
    return res.data;
  }

  async fetchUserInitiators(): Promise<ApiResponseArray<UserInitiator>> {
    const res = await this.client.get<ApiResponseArray<UserInitiator>>(
      '/v2/fund-level-hedging/user-initiators/',
    );
    return res.data;
  }

  async uploadTradeRequestData(
    payload: UploadTradeRequestPayload,
  ): Promise<ApiResponse<any>> {
    const form = new FormData();
    form.append('file', payload.file, payload.file.name);
    form.append('trades_submitted_by', String(payload.trades_submitted_by));

    const res = await this.client.post<ApiResponse<any>>(
      '/v2/fund-level-hedging/upload-trade-request-data/',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data;
  }

  async createTradeNotification(
    payload: BulkTradeNotificationsPayload,
  ): Promise<ApiResponse<string>> {
    const res = await this.client.post<ApiResponse<string>>(
      '/v2/fund-level-hedging/trade-notifications/',
      payload,
    );
    return res.data;
  }

  async downloadDocumentForFund(
    payload: DownloadDocumentForFundPayload,
  ): Promise<ApiResponse<ReportFile>> {
    const res = await this.client.post<ApiResponse<ReportFile>>(
      '/v2/fund-level-hedging/download-document-for-fund/',
      payload,
    );
    return res.data;
  }

  async insertSentForExecutionStatus(
    payload: InsertSentForExecutionStatusPayload,
  ): Promise<ApiResponse<string>> {
    const res = await this.client.post<ApiResponse<string>>(
      '/v2/fund-level-hedging/insert-sent-for-execution-status/',
      payload,
    );
    return res.data;
  }

  async insertClientApprovedStatus(
    payload: InsertClientApprovedStatusPayload,
  ): Promise<ApiResponse<string>> {
    const res = await this.client.post<ApiResponse<string>>(
      '/v2/fund-level-hedging/insert-client-approved-status/',
      payload,
    );
    return res.data;
  }

  async insertTradeRequestStatus(
    payload: NewTradeRequestStatusPayload,
  ): Promise<ApiResponse<any>> {
    const res = await this.client.post<ApiResponse<any>>(
      '/v2/fund-level-hedging/insert-trade-request-status/',
      payload,
    );
    return res.data;
  }

  async insertPMCRMApprovedStatus(
    payload: InsertPMCRMApprovedStatusPayload,
  ): Promise<ApiResponse<string>> {
    const res = await this.client.post<ApiResponse<string>>(
      '/v2/fund-level-hedging/insert-pmc-rm-approved-status/',
      payload,
    );
    return res.data;
  }

  async updateTradeRequests(
    payload: UpdateTradeRequestPayload,
  ): Promise<ApiResponse<UpdateTradeRequestResponse>> {
    const res = await this.client.post<ApiResponse<UpdateTradeRequestResponse>>(
      '/v2/fund-level-hedging/update-trade-requests/',
      payload,
    );
    return res.data;
  }

  async approvedTradeRequestsGrouping(
    payload: ApprovedTradeRequestGroupingPayload,
  ): Promise<ApiResponse<string>> {
    const res = await this.client.post<ApiResponse<string>>(
      '/v2/fund-level-hedging/approved-trade-requests-grouping/',
      payload,
    );
    return res.data;
  }

  async fetchApprovalUsers(): Promise<ApiResponseArray<ApprovalUser>> {
    const res = await this.client.get<ApiResponseArray<ApprovalUser>>(
      '/v2/fund-level-hedging/approval-users/',
    );
    return res.data;
  }

  async removeTradeRequestsGrouping(
    payload: RemoveTradeRequestGroupingPayload,
  ): Promise<ApiResponse<string>> {
    const res = await this.client.post<ApiResponse<string>>(
      '/v2/fund-level-hedging/remove-trade-requests-grouping/',
      payload,
    );
    return res.data;
  }

  async fetchTradeNotificationFieldExclusions(): Promise<FieldExclusionData> {
    const res = await this.client.get<FieldExclusionData>(
      '/v2/fund-level-hedging/trade-notification/field-exclusions/',
    );
    return res.data;
  }

  async fetchWorkflowStats(): Promise<ApiResponse<WorkflowStats>> {
    const res = await this.client.get<ApiResponse<WorkflowStats>>(
      '/v2/fund-level-hedging/get-all-workflow-stats/',
    );
    return res.data;
  }

  async createUserInitiator(
    payload: UserInitiator,
  ): Promise<ApiResponse<UserInitiator>> {
    const res = await this.client.post<ApiResponse<UserInitiator>>(
      '/v2/fund-level-hedging/user-initiators/create/',
      payload,
    );
    return res.data;
  }

  async fetchLegalEntities(): Promise<ApiResponseArray<LegalEntity>> {
    const res = await this.client.get<ApiResponseArray<LegalEntity>>(
      '/v2/fund-level-hedging/bank-comp-entity-relationships/',
    );
    return res.data;
  }

  async updateTradeRequestStatus(
    payload: UpdateTradeRequestStatusPayload,
  ): Promise<ApiResponse<any>> {
    const res = await this.client.post<ApiResponse<any>>(
      '/v2/fund-level-hedging/update-trade-request-status/',
      payload,
    );
    return res.data;
  }

  async downloadExecutionReport(
    payload: DownloadExecutionReportRequestPayload,
  ): Promise<ApiResponse<ReportFile>> {
    const res = await this.client.post<ApiResponse<ReportFile>>(
      '/v2/fund-level-hedging/download-trades-for-manual-execution-report/',
      payload,
    );
    return res.data;
  }

  async downloadExecutedTradesReport(
    payload: DownloadExecutedTradesReportRequestPayload,
  ): Promise<ApiResponse<ReportFile>> {
    const res = await this.client.post<ApiResponse<ReportFile>>(
      '/v2/reporting/download-trade-notification-execution-report/',
      payload,
    );
    return res.data;
  }

  async downloadAcceptanceReport(
    payload: DownloadExecutionReportRequestPayload,
  ): Promise<ApiResponse<ReportFile>> {
    const res = await this.client.post<ApiResponse<ReportFile>>(
      '/v2/fund-level-hedging/download-trades-for-acceptance-report/',
      payload,
    );
    return res.data;
  }

  async submitTransactionFiles(
    payload: TransactionFilesRequestPayload,
  ): Promise<ApiResponse<string>> {
    const res = await this.client.post<ApiResponse<string>>(
      '/v2/file-management/transactions/files/',
      payload,
    );
    return res.data;
  }

  async submitTransactionFilesForTransaction(
    tradeRequestId: number,
    payload: Omit<TransactionFilesPostRequestPayload, 'transaction_id'>,
  ): Promise<ApiResponse<any>> {
    const url = `/v2/file-management/transaction/${tradeRequestId}/files/`;
    const body: TransactionFilesPostRequestPayload = {
      transaction_id: tradeRequestId,
      ...payload,
    };

    const res = await this.client.post<ApiResponse<any>>(url, body);
    return res.data;
  }

  async getDocumentsForTransaction(
    tradeRequestId: number,
  ): Promise<ApiResponse<any>> {
    const url = `/v2/file-management/transaction/${tradeRequestId}/files/`;

    const res = await this.client.get<ApiResponse<any>>(url);
    return res.data;
  }

  async fetchDealNameHedgeOptions(): Promise<ApiResponseArray<any>> {
    const res = await this.client.get<ApiResponseArray<any>>(
      '/v2/fund-level-hedging/deal-name-hedge/',
    );
    return res.data;
  }

  async hideSelectedTrades(
    payload: HideSelectedTradesPayload,
  ): Promise<ApiResponse<string>> {
    const res = await this.client.post<ApiResponse<string>>(
      '/v2/fund-level-hedging/insert-remove-from-display-status/',
      payload,
    );
    return res.data;
  }

  async fetchFilteredFundCompanies(): Promise<ApiResponseArray<FundCompany>> {
    const res = await this.client.get<ApiResponseArray<FundCompany>>(
      '/v2/fund-level-hedging/fund-companies-filtered/',
    );
    return res.data;
  }

  async fetchFilteredClassificationTags(): Promise<
    ApiResponseArray<ClassificationTag>
  > {
    const res = await this.client.get<ApiResponseArray<ClassificationTag>>(
      '/v2/fund-level-hedging/classification-filtered/',
    );
    return res.data;
  }

  async uploadBlotter(file: File): Promise<ApiResponse<BlotterUploadResult>> {
    const form = new FormData();
    form.append('file', file, file.name);
    const res = await this.client.post<ApiResponse<BlotterUploadResult>>(
      '/v2/fund-level-hedging/upload-blotter/',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data;
  }

  async uploadBlotterDirect(
    file: File,
  ): Promise<ApiResponse<BlotterUploadResult>> {
    const form = new FormData();
    form.append('file', file, file.name);
    const res = await this.client.post<ApiResponse<BlotterUploadResult>>(
      '/v2/fund-level-hedging/upload-blotter-direct/',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data;
  }
}
